import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Pause, Play } from 'lucide-react'
import * as api from '../services/overviewsApi'

// F4.4: a slide template running live in an iframe, at its real 1920×1080 and
// scaled down, so the picture is the one the renderer photographs. The page is
// driven by the renderer's own contract (render + seek) over postMessage: in dev
// the app and the BFF are different origins, so there is no reaching into it.
const STAGE = { width: 1920, height: 1080 }
const seconds = (t) => `${t.toFixed(1)} s`

export function SlidePreview({ slide, at }) {
  const box = useRef(null)
  const frame = useRef(null)
  const time = useRef(0)
  const rendering = useRef(0)
  const [scale, setScale] = useState(0)
  const [t, setT] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [up, setUp] = useState(false)
  const [painted, setPainted] = useState(false)
  const [error, setError] = useState(null)

  const src = api.templatePageUrl(slide.template)
  const origin = new URL(src, window.location.href).origin
  const duration = slide.duration || 1
  const post = useCallback((m) => frame.current?.contentWindow?.postMessage(m, origin), [origin])
  const seek = useCallback((x) => {
    time.current = x
    setT(x)
    post({ slide: 'seek', t: x })
  }, [post])

  useEffect(() => {
    const onMessage = (e) => {
      if (e.source !== frame.current?.contentWindow || !e.data?.slide) return
      if (e.data.slide === 'ready') setUp(true)
      if (e.data.slide === 'error') setError(e.data.message)
      if (e.data.slide === 'rendered' && e.data.id === rendering.current) {
        post({ slide: 'seek', t: time.current })
        setPainted(true)
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [post])

  useEffect(() => {
    const node = box.current
    const fit = () => setScale(node.clientWidth / STAGE.width)
    fit()
    const watch = new ResizeObserver(fit)
    watch.observe(node)
    return () => watch.disconnect()
  }, [])

  // a different slide opens on its finished state; an edit of the same one keeps its moment
  const identity = `${slide.template}:${slide.data?.index ?? ''}`
  useEffect(() => {
    setPlaying(false)
    setError(null)
    const start = at ?? Math.max(0, duration - 1)
    time.current = start
    setT(start)
  }, [identity]) // eslint-disable-line react-hooks/exhaustive-deps

  const payload = JSON.stringify({ ...slide.data, duration })
  useEffect(() => { setUp(false); setPainted(false) }, [slide.template])
  useEffect(() => {
    if (!up) return
    time.current = Math.min(time.current, duration)
    setT(time.current)
    rendering.current += 1
    post({ slide: 'render', id: rendering.current, data: JSON.parse(payload) })
  }, [up, payload]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!playing) return undefined
    let raf
    let last = performance.now()
    const tick = (now) => {
      const next = Math.min(duration, time.current + (now - last) / 1000)
      last = now
      seek(next)
      if (next >= duration) setPlaying(false)
      else raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [playing, duration, seek])

  const toggle = () => {
    if (!playing && time.current >= duration - 0.05) seek(0)
    setPlaying(!playing)
  }

  return (
    <div className="space-y-2">
      <div ref={box} className="relative w-full aspect-video overflow-hidden rounded-lg border border-parchment-border dark:border-midnight-600 bg-[#0F1424]">
        <iframe
          key={slide.template}
          ref={frame}
          src={src}
          title={`Slide preview: ${slide.template}`}
          scrolling="no"
          tabIndex={-1}
          className="absolute top-0 left-0 border-0 pointer-events-none"
          style={{ width: STAGE.width, height: STAGE.height, transform: `scale(${scale})`, transformOrigin: '0 0' }}
        />
        {!painted && !error && <div className="absolute inset-0 grid place-items-center bg-[#0F1424]"><Loader2 className="w-5 h-5 animate-spin text-gold" /></div>}
      </div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'} className="p-2 -m-1 text-gold-deep dark:text-gold">
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <input
          type="range" min="0" max={duration} step="0.05" value={t} aria-label="Time on the slide"
          onChange={(e) => { setPlaying(false); seek(Number(e.target.value)) }}
          className="flex-1 min-w-0 accent-amber-600"
        />
        <span className="shrink-0 text-[11px] tabular-nums text-ink-soft dark:text-mist/50">{seconds(t)} / {seconds(duration)}</span>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  )
}

// Settings: every template with its sample data, as the picture the renderer makes
// (a thumbnail from the BFF) and, on a click, live with a time scrubber.
export function TemplateGallery() {
  const list = useQuery({ queryKey: ['overviews', 'templates'], queryFn: api.slideTemplates, staleTime: 60_000 })
  const [open, setOpen] = useState(null)
  const templates = (list.data?.templates ?? []).filter((x) => x.sample)
  const current = templates.find((x) => x.name === open)
  return (
    <div className="mt-6 space-y-3">
      <div>
        <div className="text-sm font-medium text-ink dark:text-mist">Slide templates</div>
        <p className="text-xs text-ink-soft dark:text-mist/60">Sample data in every template of the native slides video. The script chooses the template of a slide with its layout; pick one here to watch it move.</p>
      </div>
      {list.isLoading && <Loader2 className="w-4 h-4 animate-spin text-gold-deep dark:text-gold" />}
      {list.error && <p className="text-sm text-red-600">{list.error.message}</p>}
      {list.data && !list.data.browser && (
        <p className="text-xs text-ink-soft dark:text-mist/60">No Chrome or Edge on the server, so no thumbnails and videos fall back to ffmpeg stills. The live view below still works: it runs in this browser.</p>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {templates.map((x) => (
          <button
            key={x.name} type="button" aria-pressed={open === x.name} onClick={() => setOpen(open === x.name ? null : x.name)}
            className={`text-left rounded-lg border overflow-hidden ${open === x.name ? 'border-gold-deep dark:border-gold' : 'border-parchment-border dark:border-midnight-600'}`}
          >
            <span className="block aspect-video bg-[#0F1424]">
              {list.data.browser && <img src={api.templateThumbUrl(x.name, x.version)} alt="" loading="lazy" className="w-full h-full object-cover" />}
            </span>
            <span className="block px-2 py-1 text-xs text-ink dark:text-mist">{x.label}</span>
          </button>
        ))}
      </div>
      {current && (
        <div className="space-y-2">
          <SlidePreview slide={{ template: current.name, duration: current.sample.duration, data: current.sample.data }} at={current.sample.at} />
          <p className="text-xs text-ink-soft dark:text-mist/60">{current.used_for}</p>
        </div>
      )}
    </div>
  )
}
